import { _, ts } from '_core';

import { replaceNans } from '../formatters/helpers';

type BucketsObjectType = {
  buckets: ts.types.widgets.common.BucketValue;
  variables?: ts.types.widgets.common.ExplanatoryVariablesValue;
};

export const getRiskDecompositionLegendName = {
  PCR: '',
  FPCR: 'Factor ',
  SPCR: 'Specific ',
};

const rbicsDefaultOrder = [
  'INDUSTRY:rbics_l1_name',
  'INDUSTRY:rbics_l2_name',
  'INDUSTRY:rbics_l3_name',
  'INDUSTRY:rbics_l4_name',
];

export const getRbicsIndustryName = (value: string) => {
  if (value.includes('rbics_l1')) return 'Sector';
  if (value.includes('rbics_l2')) return 'Sub Sector';
  if (value.includes('rbics_l3')) return 'Industry';
  if (value.includes('rbics_l4')) return 'Sub Industry';

  const industryString = value.split('_');
  return `${_.upperCase(industryString[0])} ${_.startCase(industryString[1])}`;
};

export const getBucketsNames = (obj: BucketsObjectType) => {
  return { var: obj.variables.type, buckets: `${obj.buckets.type}:${obj.buckets.value}` };
};

export const getBucketOrder = (obj: BucketsObjectType, order: string) => {
  const names = getBucketsNames(obj);

  let responseOrder: any[];
  let buckets = names.buckets;

  if (buckets?.includes('rbics')) {
    buckets = rbicsDefaultOrder.find((gd) => buckets == gd);
  }

  if (order == 'selected_buckets') responseOrder = [buckets, 'variables'];
  else responseOrder = ['variables', buckets];

  return responseOrder;
};

export const getTitleByStatistics = (statistics: ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM) => {
  if (statistics == ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM.RETURNS) return 'Annual Return';
  if (statistics == ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM.RISK) return 'Annual Risk';

  return 'Annual Risk Adjusted Returns';
};

export const getBucketDefaultValue = (value: ts.enums.BUCKET_TYPES_ENUM, defaultRiskModelId?: number) => {
  if (value == ts.enums.BUCKET_TYPES_ENUM.QUANTILES) return { value: 5 };
  if (value == ts.enums.BUCKET_TYPES_ENUM.INDUSTRY) return { value: 'rbics_l1_name' };
  if (value == ts.enums.BUCKET_TYPES_ENUM.RISK_MODEL) return { risk_model_id: defaultRiskModelId };
  return {};
};

export const prepareContributorsData = (currData: ts.types.widgets.TableData, nPerformers: number) => {
  const groupedData = _.groupBy(currData, 'history');
  const groupedSum = {} as Record<string, { TOP: string; BOTTOM: string }>;

  for (const history in groupedData) {
    const historyArray = groupedData[history];

    // Split the ordered rows into two arrays
    const topHalf = historyArray.slice(0, nPerformers);
    const bottomHalf = historyArray.slice(-nPerformers); // Select from the end of the array
    const topSum = topHalf.reduce((a, b) => a + ((b['pnl'] as number) || 0), 0) * 100;
    const bottomSum = bottomHalf.reduce((a, b) => a + ((b['pnl'] as number) || 0), 0) * 100;

    topHalf.forEach((row) => (row.performers = 'TOP'));

    bottomHalf.forEach((row) => (row.performers = 'BOTTOM'));
    groupedData[history] = [...topHalf, ...bottomHalf];
    groupedSum[history] = {
      TOP: topSum.toFixed(2),
      BOTTOM: bottomSum.toFixed(2),
    };
  }

  const pnlColumnValue = {} as Record<string, string>;

  Object.keys(groupedData).forEach((key) => {
    // calculate percentage of all values in pnl column
    const pnlCol = groupedData[key].map((d) => d['pnl'] as number).reduce((a, b) => a + b, 0);

    pnlColumnValue[key] = (pnlCol * 100).toFixed(2);
  });

  return { groupedData, pnlColumnValue, groupedSum };
};

const handleIndustryName = (handle: string | null, riskModelIndustries: ts.types.riskModel.CategoryFactor[]) => {
  if (handle && handle.includes('industry')) {
    const industryName = riskModelIndustries.find((el) => el.handle == handle)?.name;
    if (industryName) return industryName;
  }
  return handle;
};

export const portanFormat = (
  data: ts.types.widgets.TableData,
  buckets: string[],
  history: ts.enums.HISTORY_ENUM_EXTENDED,
  riskModelIndustries?: ts.types.riskModel.CategoryFactor[] | undefined
) => {
  const newData = replaceNans(data);
  let filteredData = newData.filter((el) => el.history == history);

  if (_.isEmpty(filteredData)) {
    filteredData = newData.filter((el) => el.history == ts.enums.HISTORY_ENUM_EXTENDED.FULL);
  }

  filteredData = filteredData.map((el) => {
    const firstBucket = el[buckets[0]] as string;
    const secondBucket = el[buckets[1]] as string;
    const firstBucketLabel = _.checkIsNumber(firstBucket) ? `Q${firstBucket}` : firstBucket;
    const secondBucketLabel = _.checkIsNumber(secondBucket) ? `Q${secondBucket}` : secondBucket;
    return {
      [buckets[0]]: riskModelIndustries ? handleIndustryName(firstBucketLabel, riskModelIndustries) : firstBucketLabel,
      [buckets[1]]: riskModelIndustries
        ? handleIndustryName(secondBucketLabel, riskModelIndustries)
        : secondBucketLabel,
      holdings: el.holdings,
      returns: el.returns,
    };
  });

  const orderedData = filteredData.map((item) => {
    const orderedPart = _.pick(item, buckets);

    const remainingKeys = _.difference(_.keys(item), buckets);
    const remainingPart = _.pick(item, remainingKeys);

    return { ...orderedPart, ...remainingPart };
  });

  const fixedData = orderedData.map((item) => {
    const updatedItem = { ...item };

    if (_.get(item, buckets[0]) === 0) {
      _.set(updatedItem, buckets[0], '');
    }

    if (_.get(item, buckets[1]) === 0) {
      _.set(updatedItem, buckets[1], '');
    }

    return updatedItem;
  });

  const uniqueData = _.uniqBy(fixedData, (item) => buckets.map((field) => item[field]).join('|'));

  return uniqueData;
};

export const checkForErrors = (data: ts.types.widgets.TableData, viewType: string) => {
  if (_.isEmpty(data)) {
    return 'Performance Attribution produced no results.';
  }
  if (viewType != 'attributed_returns' && !Object.keys(data[0]).includes(viewType)) {
    return 'File missing columns, a new file is required. Change the definition and regenerate to get a new one.';
  }

  return '';
};

export const getOrderedHistories = (histories: ts.enums.HISTORY_ENUM_EXTENDED[]): ts.enums.HISTORY_ENUM_EXTENDED[] => {
  const orderMap = Object.values(ts.enums.HISTORY_ENUM_EXTENDED).reduce(
    (acc, val, idx) => {
      acc[val] = idx;
      return acc;
    },
    {} as Record<string, number>
  );

  return [...histories].sort((a, b) => orderMap[b] - orderMap[a]);
};
